import type { EditorDocument } from '@/clientOnlyUtils/clientUtils';
import type { Editor } from '@tiptap/core';
import { convertTemplateToHtml } from '@/clientOnlyUtils/clientUtils';

import type { SvrDocument } from '@poseidon/types/svr/svrDocument.type';
import { cn } from '@poseidon/components/cn';

import { isNotEmpty } from '@harmonizeai/poseidon-shared';

// Conditionally import SCSS to avoid breaking Cypress component tests
// Cypress's style-loader tries to inject into document.head before DOM is ready
let styles: Record<string, string> = {};
if (typeof window !== 'undefined' && !(window as { Cypress?: unknown }).Cypress) {
  // Only import SCSS in real browser, not in Cypress component tests
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  styles = (require('../styles/quillErrors.module.scss') as { default: Record<string, string> }).default ?? {};
}

export type MentionType = 'loom' | 'documents' | 'atVariables' | 'snippets' | 'comments';

export type QuillSuggestItemType = {
  id: string;
  value?: string;
  imageUrl?: string;
  fileUrl?: string;
  name?: string;
  html?: string;
  userId?: string;
  tags?: string[];
};

export type QuillCurrentState = {
  active: boolean;
  composing: boolean;
  decorationId: string;
  query: string;
  range: { from: number; to: number };
  text: string;
};

type ConfigType = {
  showMyVariablesErrors?: boolean;
  showUndefinedDocuments?: boolean;
  showContactVariablesErrors?: boolean;
  showCurlyBracketsErrors?: boolean;
  hideTooltips?: boolean;
  showDocumentUrls?: boolean;
};

export const getQuillClasses = (config: ConfigType): string => {
  const {
    showMyVariablesErrors,
    showContactVariablesErrors,
    showCurlyBracketsErrors,
    showUndefinedDocuments,
    hideTooltips = false,
    showDocumentUrls = true,
  } = config;
  return cn(
    styles.wrapper,
    showMyVariablesErrors ? styles.showMyVariablesErrors : undefined,
    showContactVariablesErrors ? styles.showContactVariablesErrors : undefined,
    showCurlyBracketsErrors ? styles.showCurlyBracketsErrors : undefined,
    showUndefinedDocuments ? styles.showUndefinedDocuments : undefined,
    showDocumentUrls ? styles.showDocumentUrls : undefined,
    hideTooltips ? styles.hideTooltips : undefined,
  );
};

export const convertQuillTemplateToTipTap = (
  html: string,
  atVariables?: Record<string, string>,
  documents?: SvrDocument[],
) => {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  wrapper.querySelectorAll('.mention').forEach((el) => {
    el.removeAttribute('data-index');
    el.removeAttribute('data-denotation-char');
    el.setAttribute('data-type', 'mention');
    el.classList.remove('variable-error');
    el.classList.remove('mention-error');
    if (el.getAttribute('data-id') === '{Comment}') {
      el.setAttribute('data-label', `{${el.getAttribute('data-value') ?? ''}}`);
    }
    el.removeAttribute('data-value');
  });

  const renameTagNames = (replaceContent: boolean, ...tags: string[]) => {
    const elements: HTMLElement[] = [];
    tags.forEach((tag) => {
      const elem = wrapper.querySelectorAll<HTMLElement>(tag);
      elem.forEach((el) => elements.push(el));
    });
    if (elements.length === 0) return;
    elements.forEach((el) => {
      if (replaceContent) {
        const parent = el.parentNode;
        while (el.firstChild) {
          parent?.insertBefore(el.firstChild, el);
        }
        parent?.removeChild(el);
      } else {
        const tipTapTag = document.createElement('p');

        for (let i = 0, l = el.attributes.length; i < l; i++) {
          const nodeName = el.attributes.item(i)?.nodeName;
          const nodeValue = el.attributes.item(i)?.nodeValue;

          if (nodeName != null && nodeValue != null) tipTapTag.setAttribute(nodeName, nodeValue);
        }
        // Persist contents
        tipTapTag.innerHTML = el.innerHTML;

        // Switch!
        el.parentNode?.replaceChild(tipTapTag, el);
      }
    });

    renameTagNames(true, 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'u');
  };

  renameTagNames(false, 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'u');

  const content = wrapper.innerHTML;

  if (atVariables && documents) {
    return convertTemplateToHtml(content, atVariables, documents)[0];
  } else {
    return content;
  }
};

export const getMentionNameByTipTapStateKey = (stateKey?: string) => {
  if (!stateKey) return;
  if (stateKey.startsWith('mention #')) return 'loom';
  if (stateKey.startsWith('mention /')) return 'documents';
  if (stateKey.startsWith('mention @')) return 'atVariables';
  if (stateKey.startsWith('mention `')) return 'snippets';
  if (stateKey.startsWith('mention {')) return 'comments';
};

export type CustomVariableType = Exclude<ReturnType<typeof getMentionNameByTipTapStateKey>, undefined>;

export const insertTipTapMention = (
  item: QuillSuggestItemType | undefined,
  editor: Editor,
  customVariableType?: CustomVariableType,
) => {
  if (!item) return;
  let currentState: null | QuillCurrentState = null;
  let mentionName: null | string = null;

  // search for current active mention and it's state
  for (const [key, value] of Object.entries(editor.view.state)) {
    const active = (value as { active?: boolean })?.active;
    const mention = getMentionNameByTipTapStateKey(key);

    if (active && mention !== undefined) {
      currentState = value as QuillCurrentState;
      mentionName = mention;
    }
  }

  if ((mentionName === null || currentState === null) && !customVariableType) return;

  // get current query range and delete it if exist
  if (currentState?.range) {
    const { from, to } = currentState.range;
    editor.commands.deleteRange({ from, to });
  }

  const { html, value: label, id, imageUrl, fileUrl } = item;

  // insert snippet instead of variable
  if (html) {
    editor.commands.insertContent(html);
    return;
  }

  const type = customVariableType ? customVariableType : mentionName ? mentionName : '';

  // custom variable inserting to prevent adding a space after it
  editor
    .chain()
    .focus()
    .insertContent({
      type,
      attrs: {
        id,
        label,
        imageUrl,
        fileUrl,
      },
    })
    .run();
};

export const convertEditorValueToAlgoliaText = (editorValue: string) => {
  const htmlValue = document.createElement('div');
  htmlValue.innerHTML = editorValue;
  htmlValue.querySelectorAll('[data-id]').forEach((d) => {
    const id = d.getAttribute('data-id');
    if (id == null) {
      // unhandled variables
      return;
    }
    if (id === '{Comment}') {
      d.innerHTML = 'Comment';
    } else {
      d.innerHTML = id;
    }
  });
  return htmlValue.innerHTML
    .replaceAll(/(<([^>]+)>)/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const convertEditorValueToSimpleText = (editorValue: string) => {
  return editorValue
    .replaceAll(/(<([^>]+)>)/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const isNotTextEditorEmpty = (arg: string | undefined | null): arg is string => {
  return isNotEmpty(arg) && convertEditorValueToSimpleText(arg).trim() !== '';
};

export function getEditorValues(
  { body2, subject }: { body2?: string; subject?: string },
  atVariables?: Record<string, string>,
  documents?: SvrDocument[],
  hideError?: boolean,
): { body2?: string; subject?: string; body2HasError?: boolean; subjectHasError?: boolean } {
  const [body2Html, body2HasError] = convertTemplateToHtml(body2 || '', atVariables, documents, hideError);
  const [subjectHtml, subjectHasError] = convertTemplateToHtml(subject || '', atVariables, documents, hideError);
  return {
    body2: body2Html.trim() || '',
    subject: subjectHtml.trim() || '',
    body2HasError: body2HasError,
    subjectHasError: subjectHasError,
  };
}

export const getEditorDocuments = (documents?: SvrDocument[], privateDocs?: string[]) => {
  const newDocuments: EditorDocument[] = [...(documents ?? [])];
  if (privateDocs) {
    privateDocs.forEach((el) => {
      const newDocument = {
        id: el,
        isPrivate: true,
      } as const;
      newDocuments.push(newDocument);
    });
  }
  return newDocuments;
};

export const getDocFromEditorDocument = (document?: EditorDocument) => {
  if (document && !('isPrivate' in document)) {
    return document;
  }
};

export const isDocumentPrivate = (document?: EditorDocument) => {
  if (document && 'isPrivate' in document) {
    return true;
  }
  return false;
};
